type SerializableValue = string | number | boolean | null | undefined;

type AnalyticsEventName =
  | 'alert_drawer_view'
  | 'position_drawer_view'
  | 'exchange_connect_modal_open'
  | 'exchange_connect_click'
  | 'custom_trade_attempt';

type PositionDrawerSource =
  | 'positions_page'
  | 'alerts_page'
  | 'alert_nested'
  | 'copy_trading';

type AnalyticsEventParams = {
  alert_drawer_view: {
    userId?: string;
    alertId?: string;
    symbol?: string;
    alertType?: string;
    tier?: number | null;
    exchange?: string;
  };
  position_drawer_view: {
    userId?: string;
    positionId?: string;
    symbol?: string;
    positionType?: 'LONG' | 'SHORT';
    isClosed?: boolean;
    source?: PositionDrawerSource;
  };
  exchange_connect_modal_open: {
    userId?: string;
    exchangeCode: string;
    exchangeName: string;
    source?: 'settings';
  };
  exchange_connect_click: {
    userId?: string;
    exchangeCode: string;
    exchangeName: string;
    source?: 'settings';
  };
  custom_trade_attempt: {
    userId?: string;
    page: 'positions' | 'alerts';
    symbol?: string;
  };
};

type GtagFunction = (
  command: 'event',
  eventName: string,
  params?: Record<string, SerializableValue>
) => void;

interface AnalyticsWindow extends Window {
  gtag?: GtagFunction;
}

const getGtag = (): GtagFunction | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const gtag = (window as AnalyticsWindow).gtag;
  return typeof gtag === 'function' ? gtag : null;
};

const sanitizeParams = (
  params: Record<string, SerializableValue>
): Record<string, SerializableValue> => {
  const cleaned: Record<string, SerializableValue> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }
    if ((value as any) instanceof Date && value !== null) {
      cleaned[key] = (value as unknown as Date).toISOString();
      return;
    }
    cleaned[key] = value;
  });
  return cleaned;
};

export const trackEvent = <T extends AnalyticsEventName>(
  eventName: T,
  params?: AnalyticsEventParams[T]
): void => {
  const gtag = getGtag();
  if (!gtag) {
    return;
  }

  const cleanedParams = params ? sanitizeParams(params as Record<string, SerializableValue>) : undefined;
  gtag('event', eventName, cleanedParams);
};

export type { PositionDrawerSource, AnalyticsEventName, AnalyticsEventParams };

